/**
 * Global -> Company -> Site, with blank meaning "ask my parent".
 *
 * The PRD's rule (section 6.1, decision Q4): a value set at a level applies to
 * that level and everything under it; a *blank* value is not an override, it
 * is a pass-through to the nearest ancestor that has one. You fill in what is
 * different and nothing else.
 *
 * What counts as blank is the one thing that has to be decided exactly,
 * because the whole hierarchy turns on it:
 *
 * - `undefined`, `null`, and a string that is empty once trimmed are blank.
 * - Everything else is a value -- including `0`, `false`, and an empty list.
 *   A site that sets "HIPAA: off" has said something; a site that never
 *   touched the flag has not, and the store keeps those apart by leaving an
 *   untouched field out rather than writing a default into it.
 *
 * Levels are passed outermost first. Nothing here knows how many there are,
 * so a fourth level (a department, say) is a longer array, not a new function.
 */

export function isBlank(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

/** The level a resolved value came from, so a settings screen can say "inherited from Company". */
export interface Resolved<T> {
  value: T | undefined;
  /** Index into the levels passed in, or -1 when every level was blank. */
  from: number;
}

/** The nearest non-blank value for one key, searching from the innermost level outward. */
export function resolveOne<T extends object, K extends keyof T>(
  levels: readonly Partial<T>[],
  key: K,
): Resolved<T[K]> {
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const value = levels[i]?.[key];
    if (!isBlank(value)) return { value: value as T[K], from: i };
  }
  return { value: undefined, from: -1 };
}

/**
 * Every key, resolved. Keys every level leaves blank are absent from the
 * result rather than present as `undefined`, so a caller spreading defaults
 * underneath (`{ ...DEFAULTS, ...resolve(levels) }`) gets the default.
 */
export function resolve<T extends object>(levels: readonly Partial<T>[]): Partial<T> {
  const keys = new Set<keyof T>();
  for (const level of levels) for (const key of Object.keys(level) as (keyof T)[]) keys.add(key);

  const out: Partial<T> = {};
  for (const key of keys) {
    const { value, from } = resolveOne(levels, key);
    if (from !== -1) out[key] = value as T[keyof T];
  }
  return out;
}
